/**
 * Error logging abstraction layer
 * 
 * This module provides a tool-agnostic interface for error logging.
 * Currently implemented with Sentry, but can be swapped for any other
 * error tracking service without changing the rest of the codebase.
 */

export interface ErrorContext {
  /** User ID if available */
  userId?: string;
  /** User email if available */
  userEmail?: string;
  /** Additional context data */
  extra?: Record<string, unknown>;
  /** Tags for filtering/grouping */
  tags?: Record<string, string>;
  /** Severity level */
  level?: 'error' | 'warning' | 'info' | 'debug';
  /** Request metadata (for API routes) */
  request?: {
    url?: string;
    method?: string;
    headers?: Record<string, string>;
  };
}

export interface Logger {
  /**
   * Log an error/exception
   */
  captureException(error: Error | unknown, context?: ErrorContext): void;

  /**
   * Log a message
   */
  captureMessage(message: string, context?: ErrorContext): void;

  /**
   * Set user context for all subsequent logs
   */
  setUser(user: { id?: string; email?: string; username?: string }): void;

  /**
   * Add breadcrumb (for tracking user actions leading to errors)
   */
  addBreadcrumb(message: string, data?: Record<string, unknown>, level?: 'info' | 'warning' | 'error'): void;

  /**
   * Start a transaction for performance monitoring
   */
  startTransaction(name: string, op: string): Transaction | null;

  /**
   * Set context that will be included in all subsequent logs
   */
  setContext(key: string, context: Record<string, unknown>): void;
}

export interface Transaction {
  setStatus(status: 'ok' | 'cancelled' | 'unknown_error' | 'deadline_exceeded' | 'failed_precondition' | 'aborted' | 'out_of_range' | 'unimplemented' | 'internal_error' | 'unavailable' | 'data_loss' | 'unauthenticated'): void;
  setData(key: string, value: unknown): void;
  finish(): void;
}

/**
 * Sentry implementation of the Logger interface
 */
class SentryLogger implements Logger {
  private shouldLog(): boolean {
    // Only log in production or when explicitly enabled
    return process.env.NODE_ENV === 'production' || process.env.ENABLE_ERROR_LOGGING === 'true';
  }

  captureException(error: Error | unknown, context?: ErrorContext): void {
    if (!this.shouldLog()) {
      // In development, still log to console for debugging
      console.error('Error (not sent to logger):', error);
      if (context) {
        console.error('Context:', context);
      }
      return;
    }

    try {
      // Dynamic import to avoid issues if Sentry isn't available
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const Sentry = require('@sentry/nextjs');
      this.addContextToSentry(Sentry, context);
      Sentry.captureException(error);
    } catch {
      // Sentry not available, fallback to console
      console.error('Error:', error, context);
    }
  }

  captureMessage(message: string, context?: ErrorContext): void {
    const level = context?.level || 'info';

    if (!this.shouldLog()) {
      console.log(`[${level.toUpperCase()}]`, message, context);
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const Sentry = require('@sentry/nextjs');
      this.addContextToSentry(Sentry, context);
      
      // Use Sentry.logger for structured logs (appears in Logs view, not Issues)
      // Only use captureMessage for error-level messages that should create issues
      if (level === 'error') {
        // Error-level messages should still create issues for visibility
        Sentry.captureMessage(message, level);
      } else {
        // Use structured logging for info, warning, and debug levels
        // These will appear in the Logs view, not Issues
        const logData: Record<string, unknown> = {};
        
        // Include extra context data
        if (context?.extra) {
          Object.assign(logData, context.extra);
        }
        
        // Include tags in log data for better filtering/searching
        if (context?.tags) {
          logData.tags = context.tags;
        }
        
        // Include request context if available
        if (context?.request) {
          logData.request = context.request;
        }
        
        switch (level) {
          case 'warning':
            Sentry.logger.warn(message, logData);
            break;
          case 'debug':
            // Sentry.logger doesn't have debug, use info with debug tag
            Sentry.logger.info(message, { ...logData, _level: 'debug' });
            break;
          case 'info':
          default:
            Sentry.logger.info(message, logData);
            break;
        }
      }
    } catch {
      console.log(`[${level.toUpperCase()}]`, message, context);
    }
  }

  setUser(user: { id?: string; email?: string; username?: string }): void {
    if (!this.shouldLog()) {
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const Sentry = require('@sentry/nextjs');
      Sentry.setUser(user);
    } catch {
      // Silently fail if Sentry not available
    }
  }

  addBreadcrumb(message: string, data?: Record<string, unknown>, level: 'info' | 'warning' | 'error' = 'info'): void {
    if (!this.shouldLog()) {
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const Sentry = require('@sentry/nextjs');
      Sentry.addBreadcrumb({
        message,
        data,
        level,
      });
    } catch {
      // Silently fail if Sentry not available
    }
  }

  startTransaction(_name: string, _op: string): Transaction | null {
    if (!this.shouldLog()) {
      return null;
    }

    // For now, return a no-op transaction
    // Can be enhanced later with actual Sentry transaction support
    return {
      setStatus: () => {},
      setData: () => {},
      finish: () => {},
    };
  }

  setContext(key: string, context: Record<string, unknown>): void {
    if (!this.shouldLog()) {
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const Sentry = require('@sentry/nextjs');
      Sentry.setContext(key, context);
    } catch {
      // Silently fail if Sentry not available
    }
  }

  private addContextToSentry(Sentry: typeof import('@sentry/nextjs'), context?: ErrorContext): void {
    if (!context) return;

    // Set user context
    if (context.userId || context.userEmail) {
      Sentry.setUser({
        id: context.userId,
        email: context.userEmail,
      });
    }

    // Set tags
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        Sentry.setTag(key, value);
      });
    }

    // Set extra context
    if (context.extra) {
      Sentry.setExtras(context.extra);
    }

    // Set request context
    if (context.request) {
      Sentry.setContext('request', context.request);
    }
  }
}

/**
 * Console-only implementation (for development or fallback)
 */
class ConsoleLogger implements Logger {
  captureException(error: Error | unknown, context?: ErrorContext): void {
    console.error('Error:', error);
    if (context) {
      console.error('Context:', context);
    }
  }

  captureMessage(message: string, context?: ErrorContext): void {
    const level = context?.level || 'info';
    console.log(`[${level.toUpperCase()}]`, message);
    if (context) {
      console.log('Context:', context);
    }
  }

  setUser(user: { id?: string; email?: string; username?: string }): void {
    console.log('User set:', user);
  }

  addBreadcrumb(message: string, data?: Record<string, unknown>, level: 'info' | 'warning' | 'error' = 'info'): void {
    console.log(`[Breadcrumb:${level}]`, message, data);
  }

  startTransaction(_name: string, _op: string): Transaction | null {
    return {
      setStatus: () => {},
      setData: () => {},
      finish: () => {},
    };
  }

  setContext(key: string, context: Record<string, unknown>): void {
    console.log(`[Context:${key}]`, context);
  }
}

/**
 * Get the logger instance
 * 
 * In production, uses Sentry. In development, uses console unless
 * ENABLE_ERROR_LOGGING=true is set.
 */
function getLogger(): Logger {
  // Allow forcing console logger via env var
  if (process.env.USE_CONSOLE_LOGGER === 'true') {
    return new ConsoleLogger();
  }

  // Use Sentry logger (will only send in production or when ENABLE_ERROR_LOGGING=true)
  return new SentryLogger();
}

// Export singleton instance
export const logger = getLogger();

// Export convenience functions
export const logError = (error: Error | unknown, context?: ErrorContext) => {
  logger.captureException(error, context);
};

export const logMessage = (message: string, context?: ErrorContext) => {
  logger.captureMessage(message, context);
};
